/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import { useState } from 'react';
import { fmtDate, isSponsorshipActive, SponsoredCompanyRow } from './utils';
import { Badge, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, FormGroup, Input } from 'reactstrap';
import { useAppDispatch } from '../../reducers';
import { addSiteAlert, SiteAlertColors } from '../../slice/siteAlertsSlice';
import { BlockingLoader } from '@g17eco/atoms/loader';
import { CompanySubscriptionHistoryModal } from './partials/CompanySubscriptionHistoryModal';
import { DATE, isAfter } from '@utils/date';
import { ColumnDef } from '@tanstack/react-table';
import { BasicAlert } from '@g17eco/molecules/alert';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { LoadingPlaceholder } from '@g17eco/molecules/loader-container';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { Table } from '@g17eco/molecules/table';
import {
  useAddSponsorshipMutation,
  useCancelSponsorshipMutation,
  useLazyRefreshSubscriptionsQuery,
  useRenewSponsorshipMutation
} from '@api/portfolio-tracker';
import './styles.scss';

export interface CompanyTableProps {
  portfolioId: string;
  handleReload: () => Promise<void>;
  companiesTableData: SponsoredCompanyRow[];
  isFetching: boolean;
  referralCode: string;
}

interface HandleRenewSponsorshipParams {
  initiativeId: string;
  sponsorshipId: string;
}

export const CompanyTable = ({ referralCode, portfolioId, companiesTableData, isFetching, handleReload }: CompanyTableProps) => {
  const dispatch = useAppDispatch();

  const [renewSponsorship] = useRenewSponsorshipMutation();
  const [refreshSubscriptions] = useLazyRefreshSubscriptionsQuery();
  const [addSponsorship] = useAddSponsorshipMutation();
  const [cancelSponsorship] = useCancelSponsorshipMutation();

  const [isLoading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState<SponsoredCompanyRow | undefined>();
  const [activeId, setActiveId] = useState<string | undefined>();

  const numberSort = (a: number, b: number) => (a > b ? 1 : a < b ? -1 : 0);

  const showError = (e: Error) => {
    dispatch(
      addSiteAlert({
        timeout: 10000,
        color: SiteAlertColors.Danger,
        content: e.message ?? 'There was an error trying to complete your request',
      })
    );
  };

  const handleToggle = async (company: SponsoredCompanyRow) => {
    setLoading(true);
    if (isSponsorshipActive(company)) {
      await cancelSponsorship({ portfolioId, code: referralCode, initiativeId: company._id }).unwrap().catch(showError);
    } else {
      await addSponsorship({ portfolioId, code: referralCode, initiativeId: company._id }).unwrap().catch(showError);
    }
    await handleReload();
    setLoading(false);
  };

  const handleToggleDropdown = (id: string) => {
    if (activeId === id) {
      setActiveId(undefined);
    } else {
      setActiveId(id);
    }
  };

  const handleRefresh = async (initiativeId: string) => {
    await refreshSubscriptions({ portfolioId, initiativeId }).unwrap().catch(showError);
    await handleReload();
  };

  const handleRenewSponsorship = async ({ initiativeId, sponsorshipId }: HandleRenewSponsorshipParams) => {
    await renewSponsorship({ portfolioId, initiativeId, sponsorshipId, code: referralCode }).unwrap()
      .then((response) => {
        dispatch(
          addSiteAlert({
            timeout: 10000,
            color: SiteAlertColors.Success,
            content: response.message ?? 'Sponsorship renewed successfully',
          })
        );
      })
      .catch(showError);
    await handleReload();
  }

  const tableColumns: ColumnDef<SponsoredCompanyRow>[] = [
    {
      id: 'toggle',
      header: 'Sponsor',
      meta: {
        headerProps: {
          className: 'col-md',
        },
      },
      cell: (c) => (
        <SimpleTooltip
          text={
            !c.row.original.canBeSponsored
              ? 'This company cannot be sponsored because they have an upgraded subscription. Please contact support for more info.'
              : undefined
          }
        >
          <FormGroup switch>
            <Input
              type='switch'
              role='switch'
              defaultChecked={isSponsorshipActive(c.row.original)}
              onChange={() => handleToggle(c.row.original)}
              disabled={!c.row.original.canBeSponsored}
            />
          </FormGroup>
        </SimpleTooltip>
      ),
    },
    {
      accessorKey: 'name',
      header: () => 'Company',
      cell: (c) => {
        return (
          <div className={'d-flex align-items-center'}>
            {!c.row.original.sponsorship ? <Badge color={'secondary'} className={'me-2'}>Legacy</Badge> : null}
            {c.row.original.name ?? null}
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      meta: {
        headerProps: {
          className: 'col-lg',
        },
        cellProps: {
          className: 'text-capitalize',
        },
      },
      cell: (c) => (!c.row.original.canBeSponsored ? 'upgraded' : c.row.original.status),
    },
    {
      accessorKey: 'discountStart',
      header: 'Discount Start',
      meta: {
        headerProps: {
          className: 'col-xl',
        },
        cellProps: {
          className: 'text-left',
        },
      },
      sortingFn: (a, b) => numberSort(a.original.discountStart ?? 0, b.original.discountStart ?? 0),
      cell: (c) => fmtDate(c.row.original.discountStart, DATE.MONTH_YEAR_DATE_SHORT),
    },
    {
      accessorKey: 'discountEnd',
      header: 'Discount End',
      meta: {
        headerProps: {
          className: 'col-xl',
        },
        cellProps: {
          className: 'text-left',
        },
      },
      sortingFn: (a, b) => numberSort(a.original.discountEnd ?? 0, b.original.discountEnd ?? 0),
      cell: (c) => {
        const date = fmtDate(c.row.original.discountEnd, DATE.MONTH_YEAR_DATE_SHORT);
        return (
          <SimpleTooltip>
            <span>{date}</span>
          </SimpleTooltip>
        );
      },
    },
    {
      id: 'renewalDate',
      header: 'Renewal date',
      accessorKey: 'renewalDate',
      sortUndefined: -1,
      enableSorting: true,
      meta: {
        headerProps: {
          className: 'col-xl',
        },
        cellProps: {
          className: 'text-left',
        },
      },
      sortingFn: (a, b) => numberSort(a.original.renewalDate ?? 0, b.original.renewalDate ?? 0),
      cell: (c) => {
        const { renewalDate, discountEnd, sponsorship } = c.row.original;
        if (!sponsorship) {
          const discountExpire = fmtDate(discountEnd, DATE.DEFAULT_SPACES_WITH_TIME)
          const text = `This is legacy sponsorship and require manual discount refresh before it expires on ${discountExpire}`;
          return (
            <SimpleTooltip text={text}>
              <Badge color={'warning'} className={'me-2'}>Manual</Badge>
            </SimpleTooltip>
          );
        }

        if (!renewalDate) {
          return null;
        }

        const date = fmtDate(renewalDate, DATE.DEFAULT_SPACES_WITH_TIME);
        return (
          <SimpleTooltip text={`Renewal process is scheduled to happen on ${date} (up to 6h earlier)`}>
            {fmtDate(renewalDate, DATE.MONTH_YEAR_DATE_SHORT)} <i className='fa fa-info-circle' />
          </SimpleTooltip>
        )
      },
    },
    {
      id: 'links',
      accessorKey: 'subscriptions',
      header: () => null,
      meta: {
        headerProps: {
          className: 'col-sm',
        },
        cellProps: {
          className: 'text-nowrap',
        },
      },
      cell: (c) => {
        const { sponsorship, _id, canBeSponsored } = c.row.original;
        return (
          <Dropdown isOpen={_id === activeId} toggle={() => handleToggleDropdown(_id)}>
            <DropdownToggle className='px-2'>
              <i className='fa fa-bars' />
            </DropdownToggle>
            <DropdownMenu container={'body'}>
              <DropdownItem size='sm' color='secondary' onClick={() => handleRefresh(_id)}>
                <i className='fal fa-arrows-rotate  mr-2' /> Refresh remote subscriptions
              </DropdownItem>
              {!canBeSponsored ? null : (
                <DropdownItem onClick={() => setShowDetails(c.row.original)}>
                  <i className={'fal fa-eye icon mr-2'} /> Preview sponsorship
                </DropdownItem>
              )}

              {sponsorship ? (
                <DropdownItem disabled={isAfter(sponsorship.cancelDate)} onClick={() => handleRenewSponsorship({
                  sponsorshipId: sponsorship._id,
                  initiativeId: _id,
                })}>
                  <i className='fal fa-arrows-rotate  mr-2' />
                  Sponsorship Renew
                  <FeatureStability className={'ms-2'} stability={'internal'} />
                </DropdownItem>
              ) : null}
            </DropdownMenu>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      {isLoading ? <BlockingLoader /> : null}
      <LoadingPlaceholder isLoading={isFetching} height={40} />
      <LoadingPlaceholder isLoading={isFetching} height={40} count={5} className='mt-1 background-ThemeTextWhite'>
        <div className='company-table-container'>
          <Table
            noData={<BasicAlert type={'primary'}>You have no companies sponsored using this referral code</BasicAlert>}
            columns={tableColumns}
            data={companiesTableData}
            pageSize={25} />
        </div>
        {showDetails ? (
          <CompanySubscriptionHistoryModal {...showDetails} toggle={() => setShowDetails(undefined)} />
        ) : null}
      </LoadingPlaceholder>
    </>
  );
};
